import { createContext, useContext } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { Product } from '@/features/products';
import type { Subscription, SubscriptionItemWithProduct } from '@/features/subscriptions';
import type { Company } from './api/companies.service';
import type { SubscriptionEstimate } from './utils/subscription-estimate';

/** Dados e ações partilhados entre o layout e as rotas-filho (tabs) do detalhe da empresa. */
export interface CompanyDetailsOutletContext {
  id: string;
  company: Company;
  navigate: NavigateFunction;
  staffUser: { role?: string } | null | undefined;
  accessTokenLoading: boolean;
  handleAccessCompany: () => Promise<void>;
  companyStatusPt: (c: Company) => string;
  gatewayShort: (raw: string | null | undefined) => string;
  isPurged: boolean;
  hasFinancialArchiveInOrya: boolean;
  setShowEraseFinancialDialog: (v: boolean) => void;
  setShowDestroyShellDialog: (v: boolean) => void;
  displaySubscription: Subscription | null;
  subscriptions: Subscription[];
  statusLabel: Record<string, string>;
  planCardStatus: string;
  currentPlanName?: string | null;
  formatPrice: (valueCents: number, currency?: string) => string;
  subscriptionEstimate: SubscriptionEstimate | null;
  intervalLabelPt: (interval: string | null | undefined) => string;
  statsRefreshing: boolean;
  refreshStats: () => void;
  usedUsers: number;
  usersUsageAvailable?: boolean;
  channelsUsageAvailable?: boolean;
  usersLimit: number | null;
  usedWa: number;
  waLimit: number | null;
  transcriptionMinutesUsed: number;
  transcriptionMinutesLimit: number;
  transcriptionUsagePct: number | null;
  transcriptionItem: SubscriptionItemWithProduct | undefined;
  aliciaItem: SubscriptionItemWithProduct | undefined;
  aliciaBonusDelta: string;
  setAliciaBonusDelta: (v: string) => void;
  transcriptionBonusDelta: string;
  setTranscriptionBonusDelta: (v: string) => void;
  bonusSubmitting: 'alicia' | 'transcription' | null;
  handleAddAliciaBonus: () => Promise<void>;
  handleAddTranscriptionBonus: () => Promise<void>;
  billingProrateAsaas: boolean;
  setBillingProrateAsaas: (v: boolean) => void;
  canManageSubscription: boolean;
  loadingItems: boolean;
  activeSubscription: Subscription | null;
  modules: SubscriptionItemWithProduct[];
  adicionais: SubscriptionItemWithProduct[];
  availableModules: Product[];
  availableAdicionais: Product[];
  handleAddModule: (productId: string) => Promise<void>;
  handleRemoveModule: (itemId: string) => Promise<void>;
  openEditItemDialog: (item: SubscriptionItemWithProduct) => void;
  handleRemoveAdicional: (itemId: string) => Promise<void>;
  handleAddAdicional: (productId: string, quantity: number) => Promise<void>;
  modulePricingMode: 'catalog' | 'free' | 'custom';
  setModulePricingMode: (m: 'catalog' | 'free' | 'custom') => void;
  moduleCustomPriceBrl: string;
  setModuleCustomPriceBrl: (v: string) => void;
  addonPricingMode: 'catalog' | 'free' | 'custom';
  setAddonPricingMode: (m: 'catalog' | 'free' | 'custom') => void;
  addonCustomPriceBrl: string;
  setAddonCustomPriceBrl: (v: string) => void;
  addProductId: string;
  setAddProductId: (v: string) => void;
  addQty: number;
  setAddQty: (v: number) => void;
  actionLoading: string | null;
  editItemLoading: boolean;
}

const CompanyDetailsContext = createContext<CompanyDetailsOutletContext | null>(null);

export function CompanyDetailsProvider({
  value,
  children,
}: {
  value: CompanyDetailsOutletContext;
  children: React.ReactNode;
}) {
  return <CompanyDetailsContext.Provider value={value}>{children}</CompanyDetailsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook de contexto, não componente
export function useCompanyDetails() {
  const v = useContext(CompanyDetailsContext);
  if (!v) throw new Error('useCompanyDetails deve ser usado dentro de CompanyDetailsLayout.');
  return v;
}
